import type { Patient, Reference } from '@medplum/fhirtypes'
import type { DateString } from '@seen/core/dates'
import { ExtendedObservation } from '@seen/core/models/observation'
import { getSearchKey, normalizeQuery } from '@seen/core/search'
import {
  type UseMutationResult,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import { cacheKeys } from '#src/hooks/cacheKeys.ts'
import { useMedplum } from '#src/hooks/useMedplum.ts'
import { DAY_CENTER_DAY_NOTE_CODE } from '#src/hooks/useStartDayCenterDayNote.ts'

// Discards a started Day Center note so the day offers the start buttons again.
//
// The note is marked `entered-in-error` rather than blanked or deleted. The day
// search and the "Start from previous" lookback both exclude that status, so
// the row genuinely sees no note, and the discarded content stays recoverable
// in the resource history. Blanking would also PATCH `valueString: ""`, which
// Medplum rejects: FHIR has no empty string.
//
// Only a preliminary note can be discarded; anything else has been signed or
// finalized and is part of the chart.
export function useResetDayCenterDayNote({
  participantReference,
  selectedDate,
}: {
  participantReference: Reference<Patient> | undefined
  selectedDate: DateString
}): UseMutationResult<void, unknown, ExtendedObservation> {
  const medplum = useMedplum()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (note) => {
      if (!note.isPreliminary) {
        throw new Error(
          `Refusing to reset a Day Center note with status "${note.status}"`
        )
      }
      // The `test` op makes the status check atomic on the server: if the note
      // was signed after this client last read it, the whole patch fails and
      // nothing is discarded.
      await medplum.patchResource('Observation', note.id, [
        { op: 'test', path: '/status', value: 'preliminary' },
        { op: 'replace', path: '/status', value: 'entered-in-error' },
      ])
    },
    onSuccess: (_, note) => {
      // Both entries the row reads a note from are cleared directly, so it
      // returns to the start buttons now rather than after the refetch.
      queryClient.setQueryData(
        cacheKeys.dayCenterDayNoteStarted(
          participantReference?.reference,
          selectedDate
        ),
        null
      )
      if (participantReference) {
        const dayQuery = ExtendedObservation.searchQuery({
          subject: participantReference,
          code: DAY_CENTER_DAY_NOTE_CODE,
          date: selectedDate,
        })
        queryClient.setQueryData(
          cacheKeys.searchOneExtended(
            'Observation',
            getSearchKey(
              medplum,
              'Observation',
              normalizeQuery({ ...dayQuery, _count: 1 })
            )
          ),
          null
        )
      }
      cacheKeys.invalidateMutation(queryClient, 'Observation', note.id)
    },
  })
}
